/** Matches the cron window in wrangler.toml: peak US hours, Mon-Fri
 *  14:00-17:00 UTC (US open + first 2h) and 19:00-21:00 UTC (pre-close) */
export function isMarketHours(now = new Date()): boolean {
  const utcDay = now.getUTCDay(); // 0 = Sun, 6 = Sat
  const utcHour = now.getUTCHours();
  if (utcDay === 0 || utcDay === 6) return false;
  return (utcHour >= 14 && utcHour < 17) || (utcHour >= 19 && utcHour < 21);
}

// ---------------------------------------------------------------------------
// Exchange-specific market hours
// ---------------------------------------------------------------------------

export interface ExchangeSchedule {
  label: string;
  openUtc: string;   // "HH:MM"
  closeUtc: string;  // "HH:MM"
  timezone: string;  // Display name
  tradingDays: number[]; // 1=Mon .. 5=Fri
}

export const EXCHANGE_HOURS: Record<string, ExchangeSchedule> = {
  NASDAQ: { label: 'NASDAQ', openUtc: '14:30', closeUtc: '21:00', timezone: 'Eastern Time (ET)', tradingDays: [1, 2, 3, 4, 5] },
  NYSE: { label: 'NYSE', openUtc: '14:30', closeUtc: '21:00', timezone: 'Eastern Time (ET)', tradingDays: [1, 2, 3, 4, 5] },
  LSE: { label: 'London Stock Exchange', openUtc: '08:00', closeUtc: '16:30', timezone: 'GMT/BST', tradingDays: [1, 2, 3, 4, 5] },
  'Euronext Paris': { label: 'Euronext Paris', openUtc: '08:00', closeUtc: '16:30', timezone: 'CET/CEST', tradingDays: [1, 2, 3, 4, 5] },
  'Euronext Amsterdam': { label: 'Euronext Amsterdam', openUtc: '08:00', closeUtc: '16:30', timezone: 'CET/CEST', tradingDays: [1, 2, 3, 4, 5] },
  XETRA: { label: 'XETRA (Frankfurt)', openUtc: '08:00', closeUtc: '16:30', timezone: 'CET/CEST', tradingDays: [1, 2, 3, 4, 5] },
  XETR: { label: 'XETRA (Frankfurt)', openUtc: '08:00', closeUtc: '16:30', timezone: 'CET/CEST', tradingDays: [1, 2, 3, 4, 5] },
  ASX: { label: 'ASX (Sydney)', openUtc: '00:00', closeUtc: '06:00', timezone: 'AEST/AEDT', tradingDays: [1, 2, 3, 4, 5] },
  TSE: { label: 'Tokyo Stock Exchange', openUtc: '00:00', closeUtc: '06:00', timezone: 'JST', tradingDays: [1, 2, 3, 4, 5] },
  HKEX: { label: 'Hong Kong Exchange', openUtc: '01:30', closeUtc: '08:00', timezone: 'HKT', tradingDays: [1, 2, 3, 4, 5] },
};

const DEFAULT_SCHEDULE: ExchangeSchedule = {
  label: 'Market',
  openUtc: '07:00',
  closeUtc: '21:00',
  timezone: 'UTC',
  tradingDays: [1, 2, 3, 4, 5],
};

export function getExchangeHours(exchange: string): ExchangeSchedule {
  return EXCHANGE_HOURS[exchange] ?? DEFAULT_SCHEDULE;
}

function parseTime(hhmm: string): { h: number; m: number } {
  const [h, m] = hhmm.split(':').map(Number);
  return { h, m };
}

export function isExchangeOpen(exchange: string, now = new Date()): boolean {
  const schedule = getExchangeHours(exchange);
  const utcDay = now.getUTCDay();
  if (!schedule.tradingDays.includes(utcDay)) return false;

  const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  const open = parseTime(schedule.openUtc);
  const close = parseTime(schedule.closeUtc);
  const openMin = open.h * 60 + open.m;
  const closeMin = close.h * 60 + close.m;

  return utcMinutes >= openMin && utcMinutes < closeMin;
}
